import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Button,
} from '@mui/material';
import { FormControlLabel, Switch, Alert } from '@mui/material';
import { PARTICIPANT_STATUSES, TRAINING_DEPARTMENTS, participantStatusLabel } from '../../constants/trainings';
import WorkerPicker from '../common/WorkerPicker';
import { contactEmailOf } from '../../services/workerNormalizer';

const createDefaultForm = () => ({
  workerId: '',
  name: '',
  email: '',
  department: '',
  status: PARTICIPANT_STATUSES[0],
});

/**
 * Pjesëmarrësi zgjidhet nga stafi, nuk shtypet me dorë.
 *
 * Fusha ishte tekst i lirë: emri, email-i dhe departamenti shkruheshin nga e
 * para për dikë që ekziston tashmë te `workers`. Pasojat janë të parashikueshme
 * — «Fisnik Sejdiu» dhe «Fisnik  Sejdiu» si dy veta, një email me gabim shtypi
 * që e humb ftesën, dhe një regjistër trajnimesh që nuk lidhet dot me dosjen e
 * punëtorit kur auditori pyet kush e ka bërë trajnimin e detyrueshëm.
 *
 * Trajnimet kanë edhe pjesëmarrës të jashtëm, ndaj shtypja me dorë nuk u hoq:
 * rri pas një çelësi, si përjashtim i shprehur.
 */
export default function AddParticipantDialog({
  open,
  onClose,
  onSubmit,
  initialValues,
  workers = [],
  departments = [],
  title = 'Shto pjesëmarrës',
  submitLabel = 'Shto',
}) {
  const [external, setExternal] = useState(false);
  const [form, setForm] = useState(() => ({
    ...createDefaultForm(),
    ...(initialValues || {}),
  }));

  useEffect(() => {
    if (open) {
      const next = { ...createDefaultForm(), ...(initialValues || {}) };
      setForm(next);
      // Një pjesëmarrës i ruajtur pa `workerId` është i jashtëm — ose i shtypur
      // me dorë para se kjo fushë të ekzistonte; të dyja redaktohen njësoj.
      setExternal(Boolean(initialValues) && !next.workerId);
    }
  }, [initialValues, open]);

  /** Zgjedhja e punëtorit mbush vetë email-in dhe departamentin. */
  const pickWorker = (id) => {
    const w = workers.find((x) => String(x.Id ?? x.id) === String(id));
    setForm((prev) => ({
      ...prev,
      workerId: id || '',
      name: w ? `${w.FirstName ?? ''} ${w.LastName ?? ''}`.trim() : '',
      email: w ? contactEmailOf(w) : '',
      department: (w && (w.DepartmentName || w.department_name)) || prev.department || '',
    }));
  };

  // Email-i mbetet i detyrueshëm: pa të, ftesa s'ka ku të shkojë dhe rreshti
  // do të dukej i plotë ndërsa pjesëmarrësi s'merr kurrë njoftim.
  const canSubmit = useMemo(
    () => (form.name || '').trim() && (form.email || '').trim(),
    [form.email, form.name]
  );

  const update = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      ...form,
      status: form.status || PARTICIPANT_STATUSES[0],
      inviteStatus: form.inviteStatus || 'Pending',
      completionDate: form.completionDate ?? null,
      score: form.score ?? null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2} mt={1}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={<Switch size="small" checked={external}
              onChange={(e) => { setExternal(e.target.checked); pickWorker(''); }} />}
            label="Pjesëmarrës i jashtëm (jo nga stafi)"
            componentsProps={{ typography: { variant: 'body2' } }}
          />

          {!external ? (
            <>
              <WorkerPicker
                workers={workers}
                value={form.workerId}
                onChange={pickWorker}
                label="Punëtori"
                placeholder="Shkruaj emrin ose email-in…"
                required
              />
              {form.workerId && (
                <Alert severity="info" sx={{ py: 0.25, fontSize: '0.8rem' }}>
                  {form.email
                    ? `Ftesa shkon te ${form.email}.`
                    : 'Ky punëtor nuk ka email të regjistruar — ftesa nuk mund të dërgohet.'}
                  {form.department ? ` Departamenti: ${form.department}.` : ''}
                </Alert>
              )}
            </>
          ) : (
            <>
              <TextField
                autoFocus
                label="Emri"
                placeholder="Emri i pjesëmarrësit"
                value={form.name}
                onChange={update('name')}
                required
                fullWidth
              />
              <TextField
                label="Email"
                placeholder="email@kompania.com"
                type="email"
                value={form.email}
                onChange={update('email')}
                required
                fullWidth
              />
              <TextField
                select
                label="Departamenti"
                value={form.department}
                onChange={update('department')}
                fullWidth
              >
                {/* Departamentet e kompanisë; lista e shkruar me dorë mbetet
                    vetëm si rezervë kur API-ja s'kthen asgjë. */}
                {(departments.length ? departments : TRAINING_DEPARTMENTS).map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          <TextField select label="Statusi" value={form.status} onChange={update('status')} fullWidth>
            {PARTICIPANT_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {participantStatusLabel(status)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Anulo
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
